import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { BackButton, CancelButton, SubmitButton } from "../../../../Components/Utils/Buttons";
import { NuInput, NuTextArea } from "../../../../Components/Utils/Input";
import FormikSelectTwo, { FormikAsyncSelect } from "../../../../Components/Utils/Select";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import UseAddData from "../../../../Components/Hooks/useAddData";
import UseDropDown from "../../../../Components/Hooks/useDropDown";
import UseFetchCurrency from "../../../../Components/Hooks/useFetchCurrencyData";
import useFetchMataData from "../../../../Components/Hooks/useFetchMataData";
import NuAmount from "../../../../Components/Utils/NuAmount";
import axiosInstance from "../../../../Services/axiosCommon";
import Select from "react-select";
import AsyncSelect from "react-select/async";

const CreateProformaInvoice = () => {
  const currentDate = new Date().toISOString().split("T")[0];
  const { addData, isLoading, Oncancel } = UseAddData();

  // Mode: "manual", "ypr", or "cq"
  const [creationMode, setCreationMode] = useState("manual");
  const [selectedPrId, setSelectedPrId] = useState("");
  const [selectedCqId, setSelectedCqId] = useState("");
  const [isFetchingSource, setIsFetchingSource] = useState(false);

  // Async dropdown states
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);

  // Dropdown hooks
  const { value: currencyOptions } = UseFetchCurrency("/currency/drop-down");

  // Async search loader for customers
  const loadCustomerOptions = useCallback((inputValue, callback) => {
    axiosInstance
      .get("/customers/drop-down", {
        params: { search: inputValue ? inputValue.trim() : "" },
      })
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const formatted = raw.map((item) => ({
          ...item,
          value: item.id !== undefined ? item.id : item.value,
          label: item.name || item.customerName || item.label || `Customer #${item.id}`,
        }));
        callback(formatted);
      })
      .catch((err) => {
        console.error("Error fetching customer options:", err);
        callback([]);
      });
  }, []);

  // Async search loader for bank accounts
  const loadBankOptions = useCallback((inputValue, callback) => {
    axiosInstance
      .get("/banks/drop-down", {
        params: { search: inputValue ? inputValue.trim() : "" },
      })
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const formatted = raw.map((item) => {
          const label = item.name || (item.bankName ? (item.accountNumber ? `${item.bankName} - ${item.accountNumber}` : item.bankName) : (item.label || item.title || item.code || String(item.id || "")));
          return {
            ...item,
            value: item.id !== undefined ? item.id : item.value,
            label: label || `Bank #${item.id}`,
          };
        });
        callback(formatted);
      })
      .catch((err) => {
        console.error("Error fetching bank options:", err);
        callback([]);
      });
  }, []);

  // Async search loader for products
  const loadProductOptions = useCallback((inputValue, callback) => {
    axiosInstance
      .get("/items/drop-down", {
        params: { search: inputValue ? inputValue.trim() : "" },
      })
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const formatted = raw.map((item) => ({
          value: item.id,
          label: item.name,
          unit: item.unit,
        }));
        callback(formatted);
      })
      .catch((err) => {
        console.error("Error fetching product options:", err);
        callback([]);
      });
  }, []);

  const asyncSelectStyles = {
    control: (provided) => ({
      ...provided,
      border: "1px solid #e5e5e5",
      outline: "none",
      borderRadius: "10px",
      padding: "1px 0",
      color: "#000",
      textAlign: "left",
      fontWeight: "500",
      width: "100%",
      minHeight: "38px",
      backgroundColor: "#fff",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#cbd5e1",
      },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 99999,
    }),
    option: (provided, state) => ({
      ...provided,
      padding: "8px 12px",
      fontSize: "12px",
      color: state.isSelected ? "#fff" : "#000",
      backgroundColor: state.isSelected ? "#2563EB" : state.isFocused ? "#eff6ff" : "#fff",
      ":hover": {
        color: "#fff",
        backgroundColor: "#60A5FA",
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: "12px",
      color: "#000",
      fontWeight: "500",
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: "12px",
      color: "#9ca3af",
      fontWeight: "500",
    }),
  };

  // Metadata dropdowns
  const { value: paymentTermsOptions } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "PaymentTerms_Supplierqoutes"
  );
  const { value: deliveryTermsOptions } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "DeliveryTerms_Supplierqoutes"
  );
  const { value: leadTimeOptions } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "LeadTime_Supplierqoutes"
  );
  const { value: validityOptions } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "Validity_Supplierquotes"
  );
  const { value: unitOptions } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "Unit_item"
  );

  // Dynamic VAT rate from API
  const [vatRate, setVatRate] = useState(15);

  useEffect(() => {
    axiosInstance
      .get("/vat/")
      .then((res) => {
        const rate = res.data?.data?.vatRate ?? res.data?.vatRate;
        if (rate !== undefined && rate !== null && !isNaN(Number(rate))) {
          setVatRate(Number(rate));
        }
      })
      .catch((err) => {
        console.error("Error fetching VAT rate:", err);
      });
  }, []);

  // Customer addresses state
  const [customerAddresses, setCustomerAddresses] = useState([]);

  // Product Line Items state
  const [products, setProducts] = useState([
    { productId: "", quantity: null, unitPrice: null, unit: "", notes: "" },
  ]);

  const [formValues, setFormValues] = useState({
    customerId: "",
    customerAddressId: "",
    bankId: "",
    date: currentDate,
    currencyId: "",
    paymentTerms: "",
    deliveryTerms: "",
    deliveryLeadTime: "",
    validity: "",
    prId: "",
    cqId: "",
    customerPoId: null,
    vatApplicable: false,
    discountAmount: null,
    transportationCharges: null,
    remarks: "",
  });

  // Fetch customer addresses when customer is selected
  useEffect(() => {
    if (formValues.customerId) {
      axiosInstance
        .get(`/customers/${formValues.customerId}`)
        .then((res) => {
          const custData = res.data?.data || res.data;
          if (custData?.addresses && Array.isArray(custData.addresses)) {
            const addrs = custData.addresses.map((a) => ({
              value: a.id,
              label: `${a.address1 || a.streetName || ""} ${a.city || ""} ${a.country || ""}`.trim() || `Address #${a.id}`,
            }));
            setCustomerAddresses(addrs);
            if (addrs.length > 0) {
              formik.setFieldValue("customerAddressId", addrs[0].value);
            }
          } else {
            setCustomerAddresses([]);
          }
        })
        .catch((err) => console.log("Customer fetch err:", err));
    } else {
      setCustomerAddresses([]);
    }
  }, [formValues.customerId]);

  // Handle switching between Manual mode, From YPR mode, and From CQ mode
  const handleModeChange = (mode) => {
    setCreationMode(mode);
    setSelectedPrId("");
    setSelectedCqId("");
    formik.setFieldValue("prId", "");
    formik.setFieldValue("cqId", "");
    formik.setFieldValue("customerPoId", null);
    setSelectedCustomer(null);
    setSelectedBank(null);
    if (mode === "manual") {
      setFormValues((prev) => ({
        ...prev,
        prId: "",
        cqId: "",
        customerPoId: null,
        customerId: "",
        bankId: "",
      }));
      setProducts([
        { productId: "", quantity: 1, unitPrice: 0, unit: "", notes: "" },
      ]);
    } else {
      setFormValues((prev) => ({
        ...prev,
        prId: "",
        cqId: "",
        customerPoId: null,
        customerId: "",
        bankId: "",
      }));
      setProducts([]);
    }
  };

  // Handle YPR selection in "From YPR" mode
  const handleYprSelect = async (selectedOption) => {
    if (!selectedOption) {
      setSelectedPrId("");
      formik.setFieldValue("prId", "");
      setFormValues((prev) => ({ ...prev, prId: "" }));
      setProducts([]);
      return;
    }

    const prId = selectedOption.value !== undefined ? selectedOption.value : selectedOption.id;
    setSelectedPrId(prId);
    formik.setFieldValue("prId", prId);
    setFormValues((prev) => ({ ...prev, prId }));

    setIsFetchingSource(true);
    try {
      let prProducts = [];
      let prDetails = null;

      // 1. Fetch products from /PR/pr-products/${prId} (as in RFQ create)
      try {
        const res = await axiosInstance.get(`/PR/pr-products/${prId}`);
        const data = res.data?.data || res.data;
        if (Array.isArray(data) && data.length > 0) {
          prProducts = data;
        }
      } catch (err) {
        console.log("Could not fetch /PR/pr-products/${prId}", err);
      }

      // 2. Fetch PR details from /PR/${prId}
      try {
        const res2 = await axiosInstance.get(`/PR/${prId}`);
        prDetails = res2.data?.data || res2.data;
        if (prProducts.length === 0) {
          const prods = prDetails?.products || prDetails?.prProducts || prDetails?.items;
          if (Array.isArray(prods) && prods.length > 0) {
            prProducts = prods;
          }
        }
      } catch (err2) {
        console.log("Could not fetch /PR/${prId}", err2);
      }

      if (prProducts && prProducts.length > 0) {
        const mapped = prProducts.map((p) => ({
          productId: p.productId || p.itemId || p.item_id || p.id || "",
          productName: p.product_name || p.productName || p.product?.name || p.name || "",
          quantity: Number(p.quantity || p.orderedQty || p.requestedQty || 1),
          unitPrice: Number(p.unitPrice || p.unit_price || p.price || 0),
          unit: p.unit || p.product_unit || p.productUnit || "",
          notes: p.notes || "",
        }));
        setProducts(mapped);
      } else {
        setProducts([]);
      }

      // Auto-populate relevant metadata fields if available in PR
      if (prDetails?.customerId) {
        const cId = String(prDetails.customerId);
        formik.setFieldValue("customerId", cId);
        setFormValues((prev) => ({ ...prev, customerId: cId }));
        const cLabel = prDetails.customerName || prDetails.customer?.name || `Customer #${cId}`;
        setSelectedCustomer({ value: prDetails.customerId, label: cLabel });
      }
      if (prDetails?.currencyId) {
        formik.setFieldValue("currencyId", String(prDetails.currencyId));
        setFormValues((prev) => ({ ...prev, currencyId: String(prDetails.currencyId) }));
      }
      if (prDetails?.paymentTerms) {
        formik.setFieldValue("paymentTerms", prDetails.paymentTerms);
      }
      if (prDetails?.deliveryTerms) {
        formik.setFieldValue("deliveryTerms", prDetails.deliveryTerms);
      }
      if (prDetails?.deliveryLeadTime || prDetails?.leadTime) {
        formik.setFieldValue("deliveryLeadTime", prDetails.deliveryLeadTime || prDetails.leadTime);
      }
      if (prDetails?.validity) {
        formik.setFieldValue("validity", prDetails.validity);
      }
    } catch (err) {
      console.error("Error loading YPR products:", err);
    } finally {
      setIsFetchingSource(false);
    }
  };

  // Handle CQ selection in "From CQ" mode
  const handleCqSelect = async (selectedOption) => {
    if (!selectedOption) {
      setSelectedCqId("");
      formik.setFieldValue("cqId", "");
      setFormValues((prev) => ({ ...prev, cqId: "" }));
      setProducts([]);
      return;
    }

    const cqId = selectedOption.value !== undefined ? selectedOption.value : selectedOption.id;
    setSelectedCqId(cqId);
    formik.setFieldValue("cqId", cqId);
    setFormValues((prev) => ({ ...prev, cqId }));

    setIsFetchingSource(true);
    try {
      let cqProducts = [];
      let cqDetails = null;

      // 1. Fetch products from /customer-quotations/products/${cqId}
      try {
        const res = await axiosInstance.get(`/customer-quotations/products/${cqId}`);
        cqDetails = res.data?.data || res.data;
        const prods = cqDetails?.products || (Array.isArray(cqDetails) ? cqDetails : []);
        if (Array.isArray(prods) && prods.length > 0) {
          cqProducts = prods;
        }
      } catch (err) {
        console.log("Could not fetch /customer-quotations/products/${cqId}", err);
      }

      // 2. Fallback to /customer-quotations/${cqId}
      if (cqProducts.length === 0) {
        try {
          const res2 = await axiosInstance.get(`/customer-quotations/${cqId}`);
          const data2 = res2.data?.data || res2.data;
          if (data2) {
            cqDetails = { ...cqDetails, ...data2 };
            const prods2 = data2.products || data2.quoteProducts || data2.items;
            if (Array.isArray(prods2) && prods2.length > 0) {
              cqProducts = prods2;
            }
          }
        } catch (err2) {
          console.log("Could not fetch /customer-quotations/${cqId}", err2);
        }
      }

      if (cqProducts && cqProducts.length > 0) {
        const mapped = cqProducts.map((p) => ({
          productId: p.productId || p.itemId || p.item_id || p.id || "",
          productName: p.productName || p.product_name || p.product?.name || p.name || "",
          quantity: Number(p.quantity || p.quoteQty || p.orderedQty || 1),
          unitPrice: Number(p.price || p.unitPrice || p.unit_price || 0),
          unit: p.unit || p.product_unit || p.productUnit || "",
          notes: p.notes || "",
        }));
        setProducts(mapped);
      } else {
        setProducts([]);
      }

      // Auto-populate relevant metadata fields if available in CQ
      if (cqDetails?.customerId) {
        const cId = String(cqDetails.customerId);
        formik.setFieldValue("customerId", cId);
        setFormValues((prev) => ({ ...prev, customerId: cId }));
        const cLabel = cqDetails.customerName || cqDetails.customer?.name || `Customer #${cId}`;
        setSelectedCustomer({ value: cqDetails.customerId, label: cLabel });
      }
      if (cqDetails?.currencyId) {
        formik.setFieldValue("currencyId", String(cqDetails.currencyId));
        setFormValues((prev) => ({ ...prev, currencyId: String(cqDetails.currencyId) }));
      }
      if (cqDetails?.paymentTerms) {
        formik.setFieldValue("paymentTerms", cqDetails.paymentTerms);
      }
      if (cqDetails?.deliveryTerms || cqDetails?.deliverTerms) {
        formik.setFieldValue("deliveryTerms", cqDetails.deliveryTerms || cqDetails.deliverTerms);
      }
      if (cqDetails?.deliveryLeadTime || cqDetails?.leadTime) {
        formik.setFieldValue("deliveryLeadTime", cqDetails.deliveryLeadTime || cqDetails.leadTime);
      }
      if (cqDetails?.validity) {
        formik.setFieldValue("validity", cqDetails.validity);
      }
      if (cqDetails?.discount) {
        formik.setFieldValue("discountAmount", Number(cqDetails.discount) || 0);
      }
    } catch (err) {
      console.error("Error loading CQ products:", err);
    } finally {
      setIsFetchingSource(false);
    }
  };

  // Product line item handlers
  const handleAddProductRow = () => {
    setProducts((prev) => [
      ...prev,
      { productId: "", quantity: 1, unitPrice: 0, unit: "", notes: "" },
    ]);
  };

  const handleRemoveProductRow = (index) => {
    if (products.length === 1) return;
    setProducts((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleProductChange = (index, field, value) => {
    setProducts((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Calculations
  const subTotal = useMemo(() => {
    return products.reduce((acc, item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unitPrice) || 0;
      return acc + qty * price;
    }, 0);
  }, [products]);

  const discount = Number(formValues.discountAmount) || 0;
  const transportation = Number(formValues.transportationCharges) || 0;
  const taxableAmount = Math.max(0, subTotal - discount + transportation);
  const vatAmount = formValues.vatApplicable ? taxableAmount * (vatRate / 100) : 0;
  const grandTotal = taxableAmount + vatAmount;

  const validationSchema = yup.object({
    customerId: yup.string().required("Customer is required"),
    bankId: yup.string().required("Bank is required"),
    date: yup.string().required("Date is required"),
    prId:
      creationMode === "ypr"
        ? yup.string().required("Purchase Request (YPR) is required")
        : yup.mixed().nullable(),
    cqId:
      creationMode === "cq"
        ? yup.string().required("Customer Quotation (CQ) is required")
        : yup.mixed().nullable(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: formValues,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { resetForm }) => {
      // Validate product line items
      const validProducts = products.filter(
        (p) => p.productId && Number(p.quantity) > 0
      );

      if (validProducts.length === 0) {
        alert(
          creationMode === "ypr"
            ? "Please select a Purchase Request (YPR) with valid products to create a Proforma Invoice."
            : creationMode === "cq"
              ? "Please select a Customer Quotation (CQ) with valid products to create a Proforma Invoice."
              : "Please add at least one valid product item."
        );
        return;
      }

      const payload = {
        customerId: Number(values.customerId),
        bankId: Number(values.bankId),
        date: values.date,
        currencyId: values.currencyId ? Number(values.currencyId) : null,
        paymentTerms: values.paymentTerms || "",
        deliveryTerms: values.deliveryTerms || "",
        deliveryLeadTime: values.deliveryLeadTime || "",
        validity: values.validity || "",
        vatApplicable: Boolean(values.vatApplicable),
        discountAmount: Number(values.discountAmount) || 0,
        transportationCharges: Number(values.transportationCharges) || 0,
        remarks: values.remarks || "",
        products: validProducts.map((p) => ({
          productId: Number(p.productId),
          quantity: Number(p.quantity),
          unitPrice: Number(p.unitPrice) || 0,
          unit: p.unit || "",
        })),
      };

      if (creationMode === "ypr" && values.prId) {
        payload.prId = Number(values.prId) || values.prId;
      }
      if (creationMode === "cq" && values.cqId) {
        payload.cqId = Number(values.cqId) || values.cqId;
      }
      if (values.customerAddressId) {
        payload.customerAddressId = Number(values.customerAddressId);
      }

      addData("/proforma-invoice/new-pi", payload, resetForm, "/sales/proforma-invoice");
    },
  });

  const selectedCurrency = useMemo(() => {
    const selectedId = formik.values.currencyId || formValues.currencyId;
    const found = (currencyOptions || []).find(
      (c) => String(c.value) === String(selectedId)
    );
    return found?.label || "SAR";
  }, [formik.values.currencyId, formValues.currencyId, currencyOptions]);

  return (
    <div className="w-full h-full py-2 px-4">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-2">
        <Breadcrumb
          items={[
            { name: "Sales", link: "/sales/proforma-invoice" },
            { name: "Proforma Invoice", link: "/sales/proforma-invoice" },
            { name: "Create" },
          ]}
        />
        <BackButton />
      </div>

      {/* Main Form Container */}
      <form onSubmit={formik.handleSubmit}>
        <div className="w-full mx-auto md:h-[calc(100vh-145px)] bg-white rounded-md mt-4 overflow-hidden flex flex-col justify-between">
          <div className="w-full p-4 sm:p-6 overflow-y-auto space-y-6">

            {/* Creation Source Option Card */}
            <div className="bg-gradient-to-r from-teal-50/70 via-emerald-50/50 to-cyan-50/70 border border-teal-100/90 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
              <div>
                <h4 className="text-xs font-bold text-gray-800 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-600 ring-4 ring-emerald-100"></span>
                  Proforma Creation Source
                </h4>
                <p className="text-[11px] text-gray-500 mt-0.5 font-medium">
                  Choose whether to enter products manually or populate automatically from a Purchase Request (YPR) or Customer Quotation (CQ).
                </p>
              </div>
              <div className="flex items-center gap-1.5 bg-white p-1 rounded-lg border border-gray-200 shrink-0 shadow-2xs">
                <button
                  type="button"
                  onClick={() => handleModeChange("manual")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all cursor-pointer flex items-center gap-1.5 ${creationMode === "manual"
                    ? "bg-[#048178] text-white shadow-xs"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                >
                  <span>✍️</span> Manual Entry
                </button>
                <button
                  type="button"
                  onClick={() => handleModeChange("ypr")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all cursor-pointer flex items-center gap-1.5 ${creationMode === "ypr"
                    ? "bg-[#048178] text-white shadow-xs"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                >
                  <span>📋</span> From YPR
                </button>
                <button
                  type="button"
                  onClick={() => handleModeChange("cq")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all cursor-pointer flex items-center gap-1.5 ${creationMode === "cq"
                    ? "bg-[#048178] text-white shadow-xs"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                >
                  <span>📑</span> From CQ
                </button>
              </div>
            </div>

            {/* Section 1: Basic Information */}
            <div>
              <div className="border-b border-gray-100 pb-2 mb-4">
                <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  Primary Details & Parties
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Customer with Async Search */}
                <div className="w-full relative px-1 py-2">
                  <label className="text-xs 2xl:text-sm">
                    Customer <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={loadCustomerOptions}
                      value={selectedCustomer}
                      onChange={(opt) => {
                        setSelectedCustomer(opt);
                        const val = opt?.value !== undefined && opt?.value !== null ? String(opt.value) : "";
                        formik.setFieldValue("customerId", val);
                        setFormValues((prev) => ({ ...prev, customerId: val }));
                      }}
                      placeholder="Select Customer"
                      isClearable={true}
                      className="text-xs 2xl:text-sm mt-2 font-medium"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      styles={asyncSelectStyles}
                    />
                  </div>
                  {formik.errors.customerId && (
                    <span className="text-xs font-semibold text-red-500">
                      {formik.errors.customerId}
                    </span>
                  )}
                </div>

                {customerAddresses.length > 0 && (
                  <FormikSelectTwo
                    label="Customer Address"
                    placeholder="Select Address"
                    Options={customerAddresses}
                    formik={formik}
                    name="customerAddressId"
                    width="w-full"
                  />
                )}

                {/* Bank Account with Async Search */}
                <div className="w-full relative px-1 py-2">
                  <label className="text-xs 2xl:text-sm">
                    Bank Account <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={loadBankOptions}
                      value={selectedBank}
                      onChange={(opt) => {
                        setSelectedBank(opt);
                        const val = opt?.value !== undefined && opt?.value !== null ? String(opt.value) : "";
                        formik.setFieldValue("bankId", val);
                        setFormValues((prev) => ({ ...prev, bankId: val }));
                      }}
                      placeholder="Select Bank"
                      isClearable={true}
                      className="text-xs 2xl:text-sm mt-2 font-medium"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      styles={asyncSelectStyles}
                    />
                  </div>
                  {formik.errors.bankId && (
                    <span className="text-xs font-semibold text-red-500">
                      {formik.errors.bankId}
                    </span>
                  )}
                </div>

                <FormikSelectTwo
                  label="Currency"
                  placeholder="Select Currency"
                  Options={currencyOptions}
                  formik={formik}
                  name="currencyId"
                  width="w-full"
                  onChange={(opt) => {
                    formik.setFieldValue("currencyId", opt?.value || "");
                    setFormValues((prev) => ({ ...prev, currencyId: opt?.value || "" }));
                  }}
                />

                <NuInput
                  label="Date"
                  type="date"
                  formik={formik}
                  name="date"
                  isRequired={true}
                  width="w-full"
                />

                {/* YPR Dropdown when in From YPR mode */}
                {creationMode === "ypr" && (
                  <FormikAsyncSelect
                    label="Purchase Request (YPR)"
                    placeholder="Select YPR"
                    url="/PR/drop-down"
                    formik={formik}
                    name="prId"
                    isRequired={true}
                    width="w-full"
                    onChange={(opt) => handleYprSelect(opt)}
                  />
                )}

                {/* CQ Dropdown when in From CQ mode */}
                {creationMode === "cq" && (
                  <FormikAsyncSelect
                    label="Customer Quotation (CQ)"
                    placeholder="Select Customer Quotation"
                    url="/customer-quotations/drop-down"
                    formik={formik}
                    name="cqId"
                    isRequired={true}
                    width="w-full"
                    onChange={(opt) => handleCqSelect(opt)}
                  />
                )}
              </div>
            </div>

            {/* Section 2: Commercial Terms */}
            <div>
              <div className="border-b border-gray-100 pb-2 mb-4">
                <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  Commercial Terms
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <FormikSelectTwo
                  label="Payment Terms"
                  placeholder="Select Terms"
                  Options={paymentTermsOptions}
                  formik={formik}
                  name="paymentTerms"
                  width="w-full"
                />

                <FormikSelectTwo
                  label="Delivery Terms"
                  placeholder="Select Terms"
                  Options={deliveryTermsOptions}
                  formik={formik}
                  name="deliveryTerms"
                  width="w-full"
                />

                <FormikSelectTwo
                  label="Delivery Lead Time"
                  placeholder="Select Lead Time"
                  Options={leadTimeOptions}
                  formik={formik}
                  name="deliveryLeadTime"
                  width="w-full"
                />

                <FormikSelectTwo
                  label="Validity"
                  placeholder="Select Validity"
                  Options={validityOptions}
                  formik={formik}
                  name="validity"
                  width="w-full"
                />
              </div>
            </div>

            {/* Section 3: Product Line Items Table */}
            <div>
              <div className="flex justify-between items-center border-b border-gray-100 pb-2 mb-4">
                <div>
                  <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                    Products
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {creationMode === "ypr"
                      ? "Products imported from the selected YPR. You can add, edit, or remove items."
                      : creationMode === "cq"
                        ? "Products imported from the selected Customer Quotation. You can add, edit, or remove items."
                        : "Add products, quantities, unit prices, and line item notes."}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {creationMode === "ypr" && selectedPrId && (
                    <span className="px-2.5 py-1 bg-teal-50 text-teal-800 border border-teal-200 rounded-lg text-xs font-semibold flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Imported from YPR
                    </span>
                  )}
                  {creationMode === "cq" && selectedCqId && (
                    <span className="px-2.5 py-1 bg-blue-50 text-blue-800 border border-blue-200 rounded-lg text-xs font-semibold flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Imported from CQ
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={handleAddProductRow}
                    className="px-3 py-1.5 bg-[#048178] hover:bg-[#036961] text-white text-xs font-semibold rounded-lg flex items-center gap-1 transition-colors cursor-pointer"
                  >
                    <span>+</span> Add Product
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto border border-gary rounded-lg bg-white">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-baseColor-primary">
                    <tr className="border-b border-gray-200 text-xs font-medium text-white">
                      <th className="p-3 w-12 text-center text-white font-medium">S.No</th>
                      <th className="p-3 min-w-[220px] text-white font-medium">Product / Item</th>
                      <th className="p-3 min-w-[130px] w-36 text-white font-medium">Unit</th>
                      <th className="p-3 w-28 text-white font-medium">Quantity</th>
                      <th className="p-3 w-36 text-white font-medium">Unit Price</th>
                      <th className="p-3 w-32 text-right text-white font-medium">Total</th>
                      <th className="p-3 w-14 text-center text-white font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-xs">
                    {products.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-gray-400">
                          <div className="flex flex-col items-center justify-center gap-2">
                            {isFetchingSource ? (
                              <>
                                <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                                <p className="text-xs font-medium text-gray-600">
                                  {creationMode === "ypr"
                                    ? "Loading products from YPR..."
                                    : "Loading products from Customer Quotation..."}
                                </p>
                              </>
                            ) : (
                              <>
                                <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <p className="text-xs font-medium text-gray-500">
                                  {creationMode === "ypr"
                                    ? selectedPrId
                                      ? "No products found in the selected YPR."
                                      : "Please select a Purchase Request (YPR) from the dropdown above to load products."
                                    : creationMode === "cq"
                                      ? selectedCqId
                                        ? "No products found in the selected Customer Quotation."
                                        : "Please select a Customer Quotation (CQ) from the dropdown above to load products."
                                      : "No products added. Click '+ Add Product' to start."}
                                </p>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      products.map((item, idx) => {
                        const lineTotal = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
                        return (
                          <tr key={idx} className="bg-white hover:bg-gray-50/50">
                            <td className="p-3 text-center font-semibold text-gray-400">{idx + 1}</td>

                            {/* Product Selection */}
                            <td className="p-3">
                              <AsyncSelect
                                cacheOptions
                                defaultOptions
                                loadOptions={loadProductOptions}
                                value={
                                  item.productId
                                    ? {
                                      value: item.productId,
                                      label: item.productName || `Item #${item.productId}`,
                                    }
                                    : null
                                }
                                onChange={(opt) => {
                                  if (opt) {
                                    handleProductChange(idx, "productId", opt.value);
                                    handleProductChange(idx, "productName", opt.label);
                                    if (opt.unit) {
                                      handleProductChange(idx, "unit", opt.unit);
                                    }
                                  } else {
                                    handleProductChange(idx, "productId", "");
                                    handleProductChange(idx, "productName", "");
                                  }
                                }}
                                placeholder="Select product..."
                                isClearable={true}
                                className="text-xs font-medium"
                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                menuPosition="fixed"
                                styles={{
                                  control: (provided) => ({
                                    ...provided,
                                    minHeight: "36px",
                                    borderRadius: "8px",
                                    borderColor: "#e5e7eb",
                                    fontSize: "12px",
                                    backgroundColor: "#fff",
                                  }),
                                  option: (provided, state) => ({
                                    ...provided,
                                    fontSize: "12px",
                                    padding: "7px 10px",
                                    color: state.isSelected ? "#fff" : "#1f2937",
                                    backgroundColor: state.isSelected ? "#048178" : state.isFocused ? "#f0fdfa" : "#fff",
                                    ":hover": {
                                      backgroundColor: state.isSelected ? "#048178" : "#f0fdfa",
                                      color: state.isSelected ? "#fff" : "#0f766e",
                                    },
                                  }),
                                  singleValue: (provided) => ({
                                    ...provided,
                                    fontSize: "12px",
                                  }),
                                  placeholder: (provided) => ({
                                    ...provided,
                                    fontSize: "12px",
                                  }),
                                  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                                }}
                              />
                            </td>

                            {/* Unit Dropdown */}
                            <td className="p-3">
                              <Select
                                options={unitOptions || []}
                                value={
                                  (unitOptions || []).find((u) => u.value === item.unit || u.label === item.unit) ||
                                  (item.unit ? { label: item.unit, value: item.unit } : null)
                                }
                                onChange={(opt) => handleProductChange(idx, "unit", opt?.label || opt?.value || "")}
                                placeholder="Select..."
                                className="text-xs font-medium"
                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                menuPosition="fixed"
                                styles={{
                                  control: (provided) => ({
                                    ...provided,
                                    minHeight: "36px",
                                    borderRadius: "8px",
                                    borderColor: "#e5e7eb",
                                    fontSize: "12px",
                                  }),
                                  option: (provided, state) => ({
                                    ...provided,
                                    fontSize: "12px",
                                    padding: "7px 10px",
                                    color: state.isSelected ? "#fff" : "#1f2937",
                                    backgroundColor: state.isSelected ? "#048178" : state.isFocused ? "#f0fdfa" : "#fff",
                                    ":hover": {
                                      backgroundColor: state.isSelected ? "#048178" : "#f0fdfa",
                                      color: state.isSelected ? "#fff" : "#0f766e",
                                    },
                                  }),
                                  singleValue: (provided) => ({
                                    ...provided,
                                    fontSize: "12px",
                                  }),
                                  placeholder: (provided) => ({
                                    ...provided,
                                    fontSize: "12px",
                                  }),
                                  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                                }}
                              />
                            </td>

                            {/* Quantity */}
                            <td className="p-3">
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => handleProductChange(idx, "quantity", e.target.value)}
                                className="w-full h-9 px-2 text-xs font-medium border border-gray-200 rounded-lg outline-none focus:border-emerald-500"
                              />
                            </td>

                            {/* Unit Price */}
                            <td className="p-3">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.unitPrice}
                                onChange={(e) => handleProductChange(idx, "unitPrice", e.target.value)}
                                className="w-full h-9 px-2 text-xs font-medium border border-gray-200 rounded-lg outline-none focus:border-emerald-500"
                              />
                            </td>

                            {/* Line Total */}
                            <td className="p-3 text-right font-bold text-gray-800">
                              <NuAmount currency={selectedCurrency} value={lineTotal} />
                            </td>

                            {/* Delete Row */}
                            <td className="p-3 text-center">
                              <button
                                type="button"
                                onClick={() => handleRemoveProductRow(idx)}
                                disabled={products.length === 1}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 cursor-pointer"
                                title={products.length === 1 ? "At least one product is required" : "Delete Row"}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Section 4: Remarks & Financial Calculations */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pt-2">

              {/* Left Column: Remarks */}
              <div className="lg:col-span-7 space-y-4">
                <NuTextArea
                  label="Remarks & Notes"
                  placeholder="Additional remarks, payment notes, or instructions..."
                  formik={formik}
                  name="remarks"
                  new_width="w-full"
                />
              </div>

              {/* Right Column: Financial Summary Card */}
              <div className="lg:col-span-5 bg-gray-50/80 border border-gray-200/80 rounded-xl p-4 space-y-3">
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider border-b border-gray-200 pb-2">
                  Financial Summary
                </h4>

                <div className="flex justify-between items-center text-xs text-gray-600">
                  <span className="font-medium">Subtotal</span>
                  <span className="font-bold text-gray-800">
                    <NuAmount currency={selectedCurrency} value={subTotal} />
                  </span>
                </div>

                <div className="flex justify-between items-center text-xs">
                  <span className="font-medium text-gray-600">Discount Amount</span>
                  <div className="w-32">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formik.values.discountAmount}
                      onChange={(e) => {
                        formik.setFieldValue("discountAmount", e.target.value);
                        setFormValues((prev) => ({ ...prev, discountAmount: e.target.value }));
                      }}
                      placeholder="0.00"
                      className="w-full h-8 px-2 text-xs font-semibold text-right border border-gray-200 rounded-lg outline-none focus:border-emerald-500 bg-white"
                    />
                  </div>
                </div>

                <div className="flex justify-between items-center text-xs">
                  <span className="font-medium text-gray-600">Transportation Charges</span>
                  <div className="w-32">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formik.values.transportationCharges}
                      onChange={(e) => {
                        formik.setFieldValue("transportationCharges", e.target.value);
                        setFormValues((prev) => ({ ...prev, transportationCharges: e.target.value }));
                      }}
                      placeholder="0.00"
                      className="w-full h-8 px-2 text-xs font-semibold text-right border border-gray-200 rounded-lg outline-none focus:border-emerald-500 bg-white"
                    />
                  </div>
                </div>

                <div className="flex justify-between items-center text-xs text-gray-600">
                  <label htmlFor="vatApplicable" className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      id="vatApplicable"
                      checked={formik.values.vatApplicable}
                      onChange={(e) => {
                        formik.setFieldValue("vatApplicable", e.target.checked);
                        setFormValues((prev) => ({ ...prev, vatApplicable: e.target.checked }));
                      }}
                      className="h-4 w-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 cursor-pointer"
                    />
                    <span className="font-medium text-gray-700">Apply VAT ({vatRate}%)</span>
                  </label>
                  <span className="font-bold text-gray-800">
                    <NuAmount currency={selectedCurrency} value={vatAmount} />
                  </span>
                </div>

                <div className="border-t border-gray-200 pt-3 flex justify-between items-center text-sm font-bold text-emerald-800">
                  <span>Grand Total</span>
                  <span className="text-base">
                    <NuAmount currency={selectedCurrency} value={grandTotal} />
                  </span>
                </div>
              </div>

            </div>

          </div>

          {/* Form Footer Actions */}
          <div className="p-3 bg-white border-t border-gray flex justify-end items-center gap-2">
            <CancelButton handleClick={Oncancel} />
            <SubmitButton name="Save" isLoading={isLoading} />
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateProformaInvoice;
